import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';

import {debounce} from '../../../assets/scripts/utils/debounce';

import SearchResults from './search-results';

const Search = ({
  placeholder,
  rootUrl,
  resources,
  unavailableProducts,
  fields,
  showSearchSuggestions,
  searchSuggestionsTitle,
  searchSuggestions,
  resultMoneyFormat,
  clearIcon,
  instantNoResults,
  typeProductsTitle,
  typeArticlesTitle,
  viewAllString,
}) => {
  const [searchTerm, setSearchTerm] = useState();
  const [results, setResults] = useState({products: [], articles: []});
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef();

  const buildResourceString = (obj) =>
    Object.keys(obj)
      .map((param) => `resources[${param}]=${obj[param]}`)
      .join('&');

  const abortController = useRef();

  const resourceString = buildResourceString(resources);

  const fetchResults = async (term) => {
    const url = `${rootUrl}/search/suggest.json?q=${term}&${resourceString}&resources[limit_scope]=each&resources[options][unavailable_products]=${unavailableProducts}&resources[options][fields]=${fields}`;

    try {
      const response = await fetch(url, {signal: abortController.current.signal});
      if (response.ok === false) {
        throw new Error('Search response was not OK');
      }
      const json = await response.json();
      return json.resources;
    } catch (err) {
      if (err.name === 'AbortError') return null;
      window.console.error(err);
      return null;
    }
  };

  const updateResults = async (term) => {
    try {
      const searchResponse = await fetchResults(term);
      if (searchResponse !== null) {
        setResults({
          products: searchResponse.results.products || [],
          articles: searchResponse.results.articles || [],
        });

        setShowResults(true);
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  const fireSearchTermToDataLayer = (value) => {
    const term = value?.trim() ?? '';
    if (!term.length) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'twp_search',
      searchTerm: term,
    });
  };

  const debouncedUpdateResults = useCallback(
    debounce((term) => {
      updateResults(term);
      fireSearchTermToDataLayer(term);
    }, 300),
    []
  );

  useEffect(() => inputRef.current.focus(), []);

  useEffect(() => {
    if (abortController.current) {
      abortController.current.abort();
    }

    abortController.current = new AbortController();

    if (!searchTerm) {
      setShowResults(false);
      return;
    }

    debouncedUpdateResults(searchTerm);
  }, [searchTerm]);

  const updateSearchTerm = (value) => setSearchTerm(value);

  const handleChange = (evt) => {
    evt.persist();
    updateSearchTerm(evt.target.value);
  };

  const clearInput = () => {
    updateSearchTerm('');
    setSearchTerm('');
    inputRef.current.focus();
  };

  const populateSearch = (suggestion) => {
    setSearchTerm(suggestion);
  };

  return (
    <>
      <form className="search__form" action={`${rootUrl}/search`} method="GET" role="search">
        <input type="hidden" name="type" value="product" />
        <input
          type="search"
          className="search__input"
          name="q"
          value={searchTerm}
          placeholder={placeholder}
          onChange={handleChange}
          aria-label={placeholder}
          ref={inputRef}
          enterKeyHint="search"
          autoComplete="off"
        />
        {searchTerm && (
          <button type="button" className="search__clear btn-none" onClick={clearInput}>
            {clearIcon}
          </button>
        )}
      </form>

      {!searchTerm && showSearchSuggestions && searchSuggestions && (
        <div className="search__suggestions body-md-short">
          {searchSuggestionsTitle && <span>{searchSuggestionsTitle}</span>}
          {searchSuggestions.map((suggestion) => {
            const suggestionTrim = suggestion.trim();
            return (
              <button
                type="button"
                className="text-tag text-tag--arrow"
                key={suggestionTrim}
                onClick={() => populateSearch(suggestionTrim)}
              >
                {suggestionTrim}
              </button>
            );
          })}
        </div>
      )}

      {showResults && (
        <SearchResults
          searchTerm={searchTerm}
          results={results}
          rootUrl={rootUrl}
          moneyFormat={resultMoneyFormat}
          instantNoResults={instantNoResults}
          typeProductsTitle={typeProductsTitle}
          typeArticlesTitle={typeArticlesTitle}
          viewAllString={viewAllString}
        />
      )}
    </>
  );
};

// Shopify's Predictive Search API reference:
// https://shopify.dev/docs/themes/ajax-api/reference/predictive-search

Search.propTypes = {
  placeholder: PropTypes.string.isRequired,
  rootUrl: PropTypes.string.isRequired,
  resources: PropTypes.shape({
    type: PropTypes.string,
    limit: PropTypes.number,
  }).isRequired,
  unavailableProducts: PropTypes.string.isRequired,
  fields: PropTypes.string.isRequired,
  showSearchSuggestions: PropTypes.bool,
  searchSuggestionsTitle: PropTypes.string,
  searchSuggestions: PropTypes.arrayOf(PropTypes.string),
  resultMoneyFormat: PropTypes.string.isRequired,
  clearIcon: PropTypes.string,
  instantNoResults: PropTypes.string,
  typeProductsTitle: PropTypes.string,
  typeArticlesTitle: PropTypes.string,
  viewAllString: PropTypes.string,
};

export default Search;
